import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { RootState } from "../../store";
import {
  login,
  LoginParams,
  logout,
  getMemberExperienceData,
  setMemberExperienceData,
  SetMemberExperienceParams,
  sendMagicLink,
} from "./authenticationAPI";

import {
  getSubscriptionDetailsAsync,
  unPauseSubscriptionAsync,
} from "../subscriptions/subscriptionSlice";
import { addIdProtectAsync } from "../admin/adminSlice";

export type AccountType =
  | "people"
  | "property"
  | "phone"
  | "neighborhood"
  | "vehicle"
  | "aamva" //added "aamva" for AAMVA account type
  | null;

export interface MemberData {
  createDate?: string;
  customerId?: number;
  expirationDate?: string;
  firstname?: string;
  lastname?: string;
  username?: string;
  orderId?: number;
  lastFour?: string;
  cardType?: string;
  accessExpirationDate?: string;
  isCardExpired?: boolean;
  subscriptionStatus?: "ACTIVE" | "INACTIVE" | "PAUSED" | null;
  billingDate?: string;
  postalCode?: string;
  id?: number;
  fromLegacy?: boolean;
}

export interface MemberExperience {
  pdfDownload?: string;
  report1?: string;
  report2?: string;
  variant?: string;
  siteJabberPrompt?: string;
  [key: string]: any; // allow for any additional fields
}

export interface AuthenticationState {
  customerId: number | null;
  status: "idle" | "loading" | "failed";
  accountType: AccountType;
  idProtectMember: boolean;
  isIdProtectAvailable: boolean;
  memberData: MemberData;
  memberExperience: MemberExperience;
  expired: boolean;
  homeUrl: string;
  reviewsUrl: string;
  memberSupport: boolean;
  idpPasswordCheck: boolean;
  rememberUsername: boolean;
  magicLinkEmail?: string;
}

const initialState: AuthenticationState = {
  customerId: null,
  idProtectMember: false,
  isIdProtectAvailable: false,
  status: "idle",
  accountType: null,
  memberData: {
    createDate: "",
    customerId: 0,
    accessExpirationDate: undefined,
    expirationDate: "",
    firstname: "",
    lastname: "",
    username: "",
    orderId: 0,
    lastFour: "",
    cardType: "",
    isCardExpired: false,
    subscriptionStatus: null,
    billingDate: "",
    postalCode: "",
  },
  memberExperience: {},
  expired: false,
  homeUrl: "",
  reviewsUrl: "",
  memberSupport: false,
  idpPasswordCheck: false,
  rememberUsername: false,
};

export interface LogError {
  success?: boolean;
  message?: string;
}

export interface LogoutParams {
  location?: string;
  customerId?: number;
  logError?: LogError;
}
export interface MagicLinkParams {
  email: string;
}

export const loginAsync = createAsyncThunk<any, LoginParams, {}>(
  "authentication/login",
  async credentials => {
    return login(credentials);
  },
);

export const logoutAsync = createAsyncThunk<
  any,
  LogoutParams,
  { state: RootState }
>("authentication/logout", async (params, thunkAPI) => {
  const state = thunkAPI.getState();
  const customerId = state.authentication.memberData?.customerId;
  return logout({ ...params, customerId });
});
export const getMemberExperienceDataAsync = createAsyncThunk<any>(
  "authenticate/getMemberExperienceData",
  async () => {
    return getMemberExperienceData();
  },
);

export const sendMagicLinkAsync = createAsyncThunk<any, MagicLinkParams, {}>(
  "authentication/sendMagicLink",
  async credentials => {
    return sendMagicLink(credentials);
  },
);

export const setMemberExperienceDataAsync = createAsyncThunk<
  any,
  SetMemberExperienceParams
>("authenticate/setMemberExperienceData", async memberExperienceParams => {
  return setMemberExperienceData(memberExperienceParams);
});

export const authenticationSlice = createSlice({
  name: "authentication",
  initialState,
  reducers: {
    saveUser: (state, action) => {
      state.memberData = action.payload;
      state.customerId = action.payload.customerId;
    },
    saveUsername: (state, action) => {
      state.rememberUsername = action.payload;
    },
    saveMagicLinkEmail: (state, action) => {
      state.magicLinkEmail = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      /* loginAsync */
      .addCase(loginAsync.pending, state => {
        state.status = "loading";
      })
      .addCase(loginAsync.fulfilled, (state, action) => {
        state.status = "idle";
        state.customerId = action.payload.memberData.customerId;
        state.memberData = action.payload.memberData;
        state.expired = action.payload.expired;
        state.homeUrl = action.payload.homeUrl;
        state.reviewsUrl = action.payload.reviewsUrl;
        state.memberSupport = action.payload.memberSupport;
        state.idpPasswordCheck = action.payload.idpPasswordCheck;
      })
      .addCase(loginAsync.rejected, state => {
        state.status = "failed";
      })
      /* logoutAsync */
      .addCase(logoutAsync.pending, state => {
        state.status = "loading";
      })
      .addCase(logoutAsync.fulfilled, state => {
        state.customerId = null;
        state.accountType = null;
        state.memberData = {
          createDate: "",
          customerId: 0,
          expirationDate: "",
          firstname: "",
          lastname: "",
          username: state.rememberUsername ? state.memberData.username : "",
          orderId: undefined,
          lastFour: "",
          cardType: "",
          isCardExpired: false,
          subscriptionStatus: null,
          billingDate: "",
          postalCode: "",
          id: undefined,
          fromLegacy: undefined, // TODO : remove when legacy membersite gone
        };
        state.expired = false;
        state.homeUrl = "";
        state.reviewsUrl = "";
        state.memberSupport = false;
        state.idpPasswordCheck = false;
        state.status = "idle";
        state.memberExperience = {};

        if ("caches" in window) {
          caches.keys().then(names => {
            names.forEach(name => caches.delete(name));
          });
        }
      })
      .addCase(logoutAsync.rejected, state => {
        state.status = "failed";
      })
      /* handle getSubscriptionDetailsAsync */
      .addCase(getSubscriptionDetailsAsync.pending, state => {
        state.status = "loading";
      })
      .addCase(getSubscriptionDetailsAsync.fulfilled, (state, action) => {
        state.status = "idle";
        state.accountType = action.payload.accountType;

        if (action.payload.accountType === "auto") {
          state.accountType = "vehicle";
        } //for reverse compatibility

        // set member card data on memberData state
        if (action.payload.memberCardData) {
          state.memberData = {
            ...state.memberData,
            lastFour: action.payload.memberCardData.lastFour,
            cardType: action.payload.memberCardData.cardType,
            isCardExpired: action.payload.memberCardData.isCardExpired,
            accessExpirationDate: action.payload.accessExpirationDate,
            subscriptionStatus:
              action.payload.memberCardData.subscriptionStatus,
            postalCode: action.payload.memberCardData.postalCode,
          };
          state.expired = action.payload.expired;
        }

        if (action.payload.primarySubscription) {
          state.memberData = {
            ...state.memberData,
            billingDate: action.payload.primarySubscription.billingDate,
            orderId: action.payload.primarySubscription.lastOrderId,
          };
        }

        // set id protect member on state
        state.idProtectMember = action.payload.idProtectMember ? true : false;
        state.isIdProtectAvailable = action.payload.isIdProtectAvailable;
      })
      .addCase(getSubscriptionDetailsAsync.rejected, state => {
        state.status = "failed";
      })
      .addCase(addIdProtectAsync.fulfilled, state => {
        state.idProtectMember = true;
      })
      .addCase(getMemberExperienceDataAsync.pending, state => {
        state.status = "loading";
      })
      .addCase(getMemberExperienceDataAsync.fulfilled, (state, action) => {
        state.status = "idle";
        if (action.payload.memberExperienceData?.result) {
          state.memberExperience =
            action.payload.memberExperienceData.result.contents;
        } else {
          state.memberExperience = {};
        }
      })
      .addCase(getMemberExperienceDataAsync.rejected, state => {
        state.status = "failed";
      })
      .addCase(sendMagicLinkAsync.pending, state => {
        state.status = "loading";
      })
      .addCase(sendMagicLinkAsync.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(sendMagicLinkAsync.rejected, state => {
        state.status = "failed";
      });
  },
});

export const { saveUsername, saveUser, saveMagicLinkEmail } =
  authenticationSlice.actions;

export const logoutWithRedirect =
  (LogoutParams: LogoutParams) => (dispatch: any) => {
    dispatch(logoutAsync(LogoutParams));
    window.location.href = "/login";
  };

export const logoutNoRedirect =
  (LogoutParams: LogoutParams) => (dispatch: any) => {
    dispatch(logoutAsync(LogoutParams));
  };

/* Getters */
export const selectCustomerId = (state: RootState) =>
  state.authentication.customerId;
export const selectMemberData = (state: RootState) =>
  state.authentication.memberData;
export const selectHomeUrl = (state: RootState) => state.authentication.homeUrl;
export const selectAuthStatus = (state: RootState) =>
  state.authentication.status;
export const selectAccountType = (state: RootState) =>
  state.authentication.accountType;
export const selectUsername = (state: RootState) =>
  state.authentication.memberData.username;
export const selectRememberUsername = (state: RootState) =>
  state.authentication.rememberUsername;
export const selectMemberExpired = (state: RootState) =>
  state.authentication.expired;
export const selectMemberExperienceData = (state: RootState) =>
  state.authentication.memberExperience;
export const selectMemberExperienceVariant = (state: RootState) =>
  state.authentication.memberExperience?.variant;
export const selectIsPIS75Variant = createSelector(
  selectMemberExperienceVariant,
  (variant: string | undefined) => variant === "pis75",
);
export const selectAccountSubscriptionStatus = (state: RootState) =>
  state.authentication.memberData.subscriptionStatus;
export const selectMagicLinkEmail = (state: RootState) =>
  state.authentication.magicLinkEmail;
export const selectIdProtectData = createSelector(
  (state: RootState) => state.authentication.idProtectMember,
  (state: RootState) => state.authentication.isIdProtectAvailable,
  (idProtectMember: boolean, isIdProtectAvailable: boolean) => ({
    idProtectMember,
    isIdProtectAvailable,
  }),
);

export default authenticationSlice.reducer;
